import React, { useState, useEffect, useMemo } from 'react';
import logoStorj from '../assets/icons-new-bucket/storj.svg';
import logoAws from '../assets/icons-new-bucket/aws.svg';
import logoGoogle from '../assets/cloud-providers/google-cloud-icon.svg';
import logoAzure from '../assets/icons-new-bucket/azure.svg';
import logoAkave from '../assets/cloud-providers/akave-logo.png';
import logoNami from '../assets/cloud-providers/nami.svg';
import logoOther from '../assets/icons-new-bucket/other.svg';
import '../styles/dialog.css';
import {
  Typography,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog as HeadlessUIDialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { StyledButton, StyledButtonIcon } from '../components/StyledButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import TextField from '@mui/material/TextField';
import eyeSvg from '../assets/details-bucket/eye.svg';
import deleteSvg from '../assets/details-bucket/delete.svg';
import settingsSvg from '../assets/details-bucket/settings.svg';
import { ApiTypes, HttpError } from '@flashbacktech/flashbackclient/api';
import { useApi } from '../contexts/ApiContext';
import { useAuth, useWorkspaces, useSelectedWorkspace } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useWithTokenRefresh } from '../utils/api';
import { toastError, toastSuccess, toastBucketCreated, toastErrorCustom } from '../utils/toast';
import CommonFields from '../components/storage/CommonFields';
import StorageTypeFields, {
  S3FieldsType,
  GCSFieldsType,
  AzureFieldsType,
} from '../components/storage/StorageTypeFields';
import { ActionResponse } from '@flashbacktech/flashbackclient/dist/api/types/storage/storage';
import { WorkspaceTypes } from '@flashbacktech/flashbackclient/api';

function StorageUnits() {
  const { apiClient } = useApi();
  const { isAuthenticated, authState, setSelectedWorkspace } = useAuth();
  const navigate = useNavigate();
  const withTokenRefresh = useWithTokenRefresh();
  
  // Use centralized workspace hooks
  const globalWorkspaces = useWorkspaces();
  const globalSelectedWorkspaceId = useSelectedWorkspace();
  const [units, setUnits] = useState<ApiTypes.StorageUnit[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [unitStats, setUnitStats] = useState<Record<string, any>>({});
  const [nodeStatuses, setNodeStatuses] = useState<Record<string, any>>({});
  const [nodeStatsMinute, setNodeStatsMinute] = useState<Record<string, any>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showAddBucketForm, setShowAddBucketForm] = useState(false);
  const [showProviderSelection, setShowProviderSelection] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [openEditUnit, setOpenEditUnit] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<ApiTypes.StorageUnit | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [availableApiKeys, setAvailableApiKeys] = useState<ApiTypes.ProviderApiKeyDTO[]>([]);
  const [isLoadingApiKeys, setIsLoadingApiKeys] = useState(false);

  // Common fields
  const [newUnit, setNewUnit] = useState<Partial<ApiTypes.StorageUnit>>({
    name: '',
    bucket: '',
    storageType: ApiTypes.StorageType.S3,
    status: 'UNKNOWN',
    apiKeyId: undefined,
  });

  // Workspace selection for bucket creation - use globalWorkspaces from hook
  // Local state for the selected workspace (synced with global)
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>(() => {
    // Initialize from global hook value
    return globalSelectedWorkspaceId !== 'ALL' ? globalSelectedWorkspaceId : 'ALL';
  });
  const [globalWorkspaceAll, setGlobalWorkspaceAll] = useState<boolean>(() => globalSelectedWorkspaceId === 'ALL');
  const [workspaceOverride, setWorkspaceOverride] = useState<boolean>(false);
  const [initialGlobalWorkspaceAll, setInitialGlobalWorkspaceAll] = useState<boolean>(() => globalSelectedWorkspaceId === 'ALL');
  
  // Alias for backward compatibility - workspaces now comes from global hook
  const workspaces = globalWorkspaces;
  // Track last workspace change to debounce unrelated refresh events
  const lastWorkspaceChangeAtRef = React.useRef<number>(0);

  // Map StorageType to provider id for API key filter (backend uses AWS, GCP, AZURE)
  const storageTypeToProvider = (st: ApiTypes.StorageType | undefined): string | undefined => {
    if (!st) return undefined;
    switch (st) {
      case ApiTypes.StorageType.S3: return 'AWS';
      case ApiTypes.StorageType.GCS: return 'GCP';
      case ApiTypes.StorageType.BLOB: return 'AZURE';
      default: return undefined;
    }
  };

  // Load available API keys (filtered by storage capability and selected provider when set)
  const loadApiKeys = async () => {
    try {
      setIsLoadingApiKeys(true);
      const targetWorkspaceId = selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : undefined;
      const provider = storageTypeToProvider(newUnit.storageType);
      const response = await apiClient.getApiKeys({
        workspaceId: targetWorkspaceId,
        capability: ApiTypes.ProviderCapability.Storage,
        ...(provider ? { provider } : {}),
      });
      if (response && response.success) {
        setAvailableApiKeys(response.apiKeys || []);
      }
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setIsLoadingApiKeys(false);
    }
  };

  useEffect(() => {
    if (showProviderSelection || showAddBucketForm) {
      loadApiKeys();
    }
  }, [showProviderSelection, showAddBucketForm, selectedWorkspaceId, newUnit.storageType]);

  // État pour suivre si le fournisseur est "Other"
  const [isOtherProvider, setIsOtherProvider] = useState(false);
  
  // État pour le provider compatible choisi quand "Other" est sélectionné
  const [compatibleProvider, setCompatibleProvider] = useState<ApiTypes.StorageType>(ApiTypes.StorageType.S3);

  // S3-specific fields
  const [s3Fields, setS3Fields] = useState<S3FieldsType>({
    bucket: '',
    accessKey: '',
    secret: '',
    endpoint: '',
    region: '',
  });

  // GCS-specific fields
  const [gcsFields, setGcsFields] = useState<GCSFieldsType>({
    bucket: '',
    clientEmail: '',
    privateKey: '',
    endpoint: '',
  });

  // Azure-specific fields
  const [azureFields, setAzureFields] = useState<AzureFieldsType>({
    storageAccount: '',
    container: '',
    accessKey: '',
  });

  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    bucket?: string;
    key?: string;
    secret?: string;
    region?: string;
    clientEmail?: string;
    privateKey?: string;
    storageAccount?: string;
    container?: string;
    accessKey?: string;
    endpoint?: string;
  }>({});

  // Sequence guard to ensure only the latest request applies
  const loadSeqRef = React.useRef<number>(0);
  // Guard stats updates to the latest list load
  const statsSeqRef = React.useRef<number>(0);
  const nodeStatsSeqRef = React.useRef<number>(0);

  // Sync with global workspace selection from useSelectedWorkspace hook
  useEffect(() => {
    // Update local state when global selection changes
    if (globalSelectedWorkspaceId === 'ALL') {
      setGlobalWorkspaceAll(true);
      setSelectedWorkspaceId('ALL');
    } else {
      setGlobalWorkspaceAll(false);
      setSelectedWorkspaceId(globalSelectedWorkspaceId);
    }
    lastWorkspaceChangeAtRef.current = Date.now();
    // Clear current list to avoid displaying stale/unfiltered items while reloading
    setUnits([]);
    // Reset to first page
    setCurrentPage(1);
    // Reset force refresh to allow cache usage for new workspace
    setForceRefresh(false);
  }, [globalSelectedWorkspaceId]);

  // Ignore bucketsUpdated for list reloads to avoid ALL→specific race; counters or other views may still use it
  // If needed later, a separate counter refresh can listen to this without reloading the list.

  // Filter, sort and paginate units
  const { paginatedUnits, totalPages, totalFiltered } = useMemo(() => {
    // Filter by search query
    const filtered = units.filter((unit) => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        unit.name?.toLowerCase().includes(query) ||
        unit.bucket?.toLowerCase().includes(query) ||
        unit.storageType?.toLowerCase().includes(query)
      );
    });

    // Sort by creation date (most recent first)
    const sorted = [...filtered].sort((a, b) => {
      const dateA = (a as any).createdAt || (a as any).created_at || (a as any).timestamp || 0;
      const dateB = (b as any).createdAt || (b as any).created_at || (b as any).timestamp || 0;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });

    // Paginate
    const totalPages = Math.ceil(sorted.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = sorted.slice(startIndex, endIndex);

    return { paginatedUnits: paginated, totalPages, totalFiltered: sorted.length };
  }, [units, searchQuery, currentPage, itemsPerPage]);

  // Memoize unit IDs to prevent infinite loop in useEffect
  const unitIds = useMemo(() => {
    return units.map((unit) => unit.id);
  }, [units.map((unit) => unit.id).join(',')]);

  useEffect(() => {
    resetForm();
  }, []);

  // Open Edit/Delete dialog if triggered from external navigation (e.g., Overview page)
  useEffect(() => {
    if (!units || units.length === 0) return;
    try {
      const editId = localStorage.getItem('triggerEditBucketId');
      if (editId) {
        const unit = units.find((u) => u.id === editId);
        if (unit) {
          setSelectedUnit(unit);
          setNewUnit({
            name: unit.name,
            bucket: unit.bucket,
            storageType: unit.storageType,
            status: 'UNKNOWN',
            apiKeyId: unit.apiKeyId,
          });
          switch (unit.storageType) {
            case ApiTypes.StorageType.S3:
              setS3Fields({
                bucket: unit.bucket,
                accessKey: unit.key || '',
                secret: unit.secret?.key || '',
                endpoint: unit.endpoint || '',
                region: unit.region || '',
              });
              break;
            case ApiTypes.StorageType.GCS:
              setGcsFields({
                bucket: unit.bucket,
                clientEmail: unit.key || '',
                privateKey: unit.secret?.key || '',
                endpoint: unit.endpoint || '',
              });
              break;
            case ApiTypes.StorageType.BLOB:
              setAzureFields({
                storageAccount: unit.key || '',
                container: unit.bucket || '',
                accessKey: unit.secret?.key || '',
              });
              break;
          }
          setOpenEditUnit(true);
        }
        localStorage.removeItem('triggerEditBucketId');
      }

      const deleteId = localStorage.getItem('triggerDeleteBucketId');
      if (deleteId) {
        const unit = units.find((u) => u.id === deleteId);
        if (unit) {
          setSelectedUnit(unit);
          setOpenDeleteDialog(true);
        }
        localStorage.removeItem('triggerDeleteBucketId');
      }
    } catch (e) {
      // ignore
    }
  }, [units]);

  // Open Add Bucket flow when navigated from Sidebar checklist
  useEffect(() => {
    try {
      const flag = localStorage.getItem('triggerAddBucket');
      if (flag === '1') {
        // Clear the flag to avoid repeated openings
        localStorage.removeItem('triggerAddBucket');
        // Open provider selection or add form depending on your UX
        setShowProviderSelection(true);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
    }
  }, []);

  // Effet pour détecter si le fournisseur sélectionné est "Other"
  useEffect(() => {
    const selectedProvider = localStorage.getItem('selectedStorageType');
    const isOther = selectedProvider === 'OTHER';
    setIsOtherProvider(isOther);
    
    // Initialiser le compatibleProvider avec le storageType actuel si "Other" est sélectionné
    if (isOther && newUnit.storageType) {
      setCompatibleProvider(newUnit.storageType);
    }
  }, [showAddBucketForm, newUnit.storageType]);

  // Workspaces are now loaded from AuthContext via useWorkspaces() hook
  // No need to load them here - they're already cached in globalData
  
  // Apply global workspace selection update (sync with Sidebar and other views)
  const applyGlobalWorkspaceSelection = React.useCallback((wid: string) => {
    setSelectedWorkspaceId(wid);
    // Use centralized setSelectedWorkspace from AuthContext
    setSelectedWorkspace(wid);
  }, [setSelectedWorkspace]);

  // Effet pour réagir aux changements de type de stockage
  useEffect(() => {
    if (isOtherProvider) {
      // Réinitialiser les champs spécifiques au type de stockage lorsque le type change
      switch (newUnit.storageType) {
        case ApiTypes.StorageType.S3:
          setS3Fields({
            bucket: '',
            accessKey: '',
            secret: '',
            region: '',
            endpoint: '',
          });
          break;
        case ApiTypes.StorageType.GCS:
          setGcsFields({
            bucket: '',
            clientEmail: '',
            privateKey: '',
            endpoint: '',
          });
          break;
        case ApiTypes.StorageType.BLOB:
          setAzureFields({
            container: '',
            storageAccount: '',
            accessKey: '',
          });
          break;
      }
    }
  }, [newUnit.storageType, isOtherProvider]);

  useEffect(() => {
    units.forEach((unit) => { /* Empty */ });

    // Récupérer les statuts des nœuds pour chaque unité
    const fetchAllNodeStats = async () => {
      if (unitIds.length > 0) {
        await fetchNodeStats(unitIds);
      }
    };

    if (unitIds.length > 0) {
      fetchAllNodeStats();
    }

    // Rafraîchir les stats toutes les 30 secondes comme dans StorageUnitDetails.tsx
    const intervalId = setInterval(() => {
      if (unitIds.length > 0) {
        fetchNodeStats(unitIds);
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [unitIds]);

  // Fonction pour récupérer les statistiques des nœuds via l'API getUnitNodeStats
  const fetchNodeStats = async (unitIds: string[]) => {
    if (!unitIds.length) return;

    try {
      const newNodeStatuses: Record<string, any> = {};
      const newNodeStatsMinute: Record<string, any> = {};

      // Initialiser les structures pour chaque unité
      unitIds.forEach((unitId) => {
        newNodeStatuses[unitId] = {
          nodes: [],
          statusCounts: { O: 0, M: 0, D: 0 },
        };
        newNodeStatsMinute[unitId] = [];
      });

      // Créer un objet pour stocker les statuts par unitId
      const unitStatusMap: Record<string, { hasOnlineNode: boolean; hasWarningNode: boolean }> = {};

      // Initialiser le statut de chaque unité
      unitIds.forEach((id) => {
        unitStatusMap[id] = { hasOnlineNode: false, hasWarningNode: false };
      });

      // Récupérer les données pour chaque unité individuellement
      for (const unitId of unitIds) {
        try {
          // Utiliser la date du jour comme dans StorageUnitDetails.tsx
          const specificDate = new Date();
          const response = await withTokenRefresh(() =>
            apiClient.getBucketNodeStats(unitId, { day: specificDate })
          );

          if (response && response.nodeStats && Array.isArray(response.nodeStats)) {
            // Traiter les nœuds pour cette unité
            response.nodeStats.forEach((node) => {
              const nodeStatus = node.node_status || 'UNKNOWN';

              // Déterminer le statut du nœud (O, M ou D)
              let status = 'D'; // Par défaut Disconnected
              if (nodeStatus === 'ONLINE') {
                status = 'O'; // Online
                newNodeStatuses[unitId].statusCounts.O++;
                unitStatusMap[unitId].hasOnlineNode = true;
              } else if (nodeStatus === 'DISCONNECTED') {
                status = 'M'; // Medium/Warning
                newNodeStatuses[unitId].statusCounts.M++;
                unitStatusMap[unitId].hasWarningNode = true;
              } else {
                newNodeStatuses[unitId].statusCounts.D++;
              }

              // Ajouter les infos du nœud
              newNodeStatuses[unitId].nodes.push({
                id: node.ip || '',
                name: node.host
                  ? node.host.split('.')[0]
                  : `Node ${newNodeStatuses[unitId].nodes.length + 1}`,
                status,
                latency: node.avg_latency_ms || 0,
              });

              // Ajouter à la structure newNodeStatsMinute
              newNodeStatsMinute[unitId].push({
                nodeId: node.ip || '',
                unitId: unitId,
                nodeStatus: nodeStatus,
                lastUpdated: new Date(node.last_updated || Date.now()),
                latency_ms: node.avg_latency_ms || 0,
                host: node.host || '',
                version: node.version || '',
                perc_uptime: node.perc_uptime || 0,
              });
            });
          } else { /* Empty */ }
        } catch (error) { /* Ignore errors */ }
      }

      // Mettre à jour le statut de chaque unité en fonction des nodes
      const updatedUnits = [...units];
      let hasChanges = false;

      Object.keys(unitStatusMap).forEach((unitId) => {
        const unitIndex = units.findIndex((u) => u.id === unitId);
        if (unitIndex !== -1) {
          // Si au moins un node est online, le bucket est online
          if (unitStatusMap[unitId].hasOnlineNode) {
            if (updatedUnits[unitIndex].status !== 'ONLINE') {
              updatedUnits[unitIndex].status = 'ONLINE';
              hasChanges = true;
            }
          }
          // Si aucun node n'est online mais au moins un est en warning, le bucket est disconnected
          else if (unitStatusMap[unitId].hasWarningNode) {
            if (updatedUnits[unitIndex].status !== 'DISCONNECTED') {
              updatedUnits[unitIndex].status = 'DISCONNECTED';
              hasChanges = true;
            }
          }
          // Si tous les nodes sont disconnected ou s'il n'y a pas de nodes, le bucket est disconnected
          else {
            if (updatedUnits[unitIndex].status !== 'DISCONNECTED') {
              updatedUnits[unitIndex].status = 'DISCONNECTED';
              hasChanges = true;
            }
          }
        }
      });

      // Pour les unités qui n'ont pas de données, utiliser des valeurs à zéro
      unitIds.forEach((unitId) => {
        if (newNodeStatuses[unitId].nodes.length === 0) {
          // Conserver la structure mais avec des tableaux vides
          newNodeStatuses[unitId].noDataAvailable = true;

          // Mettre à jour le statut de l'unité comme disconnected si aucune donnée n'est disponible
          const unitIndex = units.findIndex((u) => u.id === unitId);
          if (unitIndex !== -1 && updatedUnits[unitIndex].status !== 'DISCONNECTED') {
            updatedUnits[unitIndex].status = 'DISCONNECTED';
            hasChanges = true;
          }
        }
      });

      // Only update units state if there are actual changes
      if (hasChanges) {
        setUnits(updatedUnits);
      }

      setNodeStatuses(newNodeStatuses);
      setNodeStatsMinute(newNodeStatsMinute);

      // Save node stats to cache
      const nodeStatsCacheKey = `buckets_node_stats_cache_${selectedWorkspaceId}`;
      try {
        localStorage.setItem(
          nodeStatsCacheKey,
          JSON.stringify({
            nodeStats: newNodeStatuses,
            timestamp: Date.now(),
          })
        );
      } catch (error) { /* Ignore errors */ }
    } catch (error) {
      // En cas d'erreur, utiliser des valeurs à zéro comme fallback
      const newNodeStatuses: Record<string, any> = {};
      const updatedUnits = [...units];
      let hasChanges = false;

      unitIds.forEach((unitId) => {
        newNodeStatuses[unitId] = generateNodeStatuses(unitId);

        // Mettre à jour le statut de l'unité comme disconnected en cas d'erreur
        const unitIndex = units.findIndex((u) => u.id === unitId);
        if (unitIndex !== -1 && updatedUnits[unitIndex].status !== 'DISCONNECTED') {
          updatedUnits[unitIndex].status = 'DISCONNECTED';
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setUnits(updatedUnits);
      }
      setNodeStatuses(newNodeStatuses);
    }
  };

  // Fonction helper pour traiter les statistiques d'une unité (définition accessible avant utilisation)
  function processV2Entity(
    entityId: string,
    totals: { requests: number; uplBytes: number; dwlBytes: number; sizeChange: number; latencyMs: number },
    stats: Record<string, any>,
  ) {
    stats[entityId] = {
      calls: totals.requests,
      storageChange: totals.sizeChange,
      storageChangeFormatted: formatBytesToGB(totals.sizeChange),
      downloadedBytes: totals.dwlBytes,
      downloadedBytesFormatted: formatBytesToGB(totals.dwlBytes),
      uploadedBytes: totals.uplBytes,
      uploadedBytesFormatted: formatBytesToGB(totals.uplBytes),
      isOnline: false,
      lastSeen: 0,
    };
  }

  // Fonction pour récupérer les statistiques des unités via l'API
  const fetchUnitStats = async (unitIds: string[]) => {
    if (!unitIds.length) return;

    const statsCacheKey = `buckets_stats_cache_${selectedWorkspaceId}`;

    try {
      const newStats: Record<string, any> = {};

      // Fetch per-bucket stats via V2 storage gateway breakdown
      const breakdownResponse = await withTokenRefresh(() =>
        apiClient.getStorageGatewayBreakdown('buckets', { scope: 'org', window: '30d' } as any)
      );

      if (breakdownResponse?.entities && Array.isArray(breakdownResponse.entities)) {
        breakdownResponse.entities.forEach((entity: any) => {
          if (entity.entityId && unitIds.includes(entity.entityId)) {
            processV2Entity(entity.entityId, entity.totals, newStats);
          }
        });
      }

      // Fallback to zeroed stats for buckets not present in V2 response
      unitIds.forEach((id) => {
        if (!newStats[id]) {
          newStats[id] = getRandomStats(id);
        }
      });

      // Guard against stale stats update
      if (statsSeqRef.current !== loadSeqRef.current) {
        return;
      }
      setUnitStats(newStats);

      // Save stats to cache
      try {
        localStorage.setItem(
          statsCacheKey,
          JSON.stringify({
            stats: newStats,
            timestamp: Date.now(),
          })
        );
      } catch (error) { /* Ignore errors */ }
    } catch (error) {
      const newStats: Record<string, any> = {};
      unitIds.forEach((id) => {
        newStats[id] = getRandomStats(id);
      });
      // Guard against stale stats update
      if (statsSeqRef.current !== loadSeqRef.current) {
        return;
      }
      setUnitStats(newStats);
    }
  };

  // Charger les buckets au chargement du composant et à chaque changement de workspace
  useEffect(() => {
    let isMounted = true;

    const loadStorageUnits = async () => {
      if (!isAuthenticated) {
        navigate('/');
        return;
      }

      // Capture current selection to protect against stale responses
      const initialSelected = selectedWorkspaceId;
      const seq = ++loadSeqRef.current;

      // Try to load from cache first (unless force refresh)
      const cacheKey = `buckets_cache_${initialSelected}`;
      const statsCacheKey = `buckets_stats_cache_${initialSelected}`;
      const nodeStatsCacheKey = `buckets_node_stats_cache_${initialSelected}`;

      if (!forceRefresh) {
        try {
          const cached = localStorage.getItem(cacheKey);
          const cachedStats = localStorage.getItem(statsCacheKey);
          const cachedNodeStats = localStorage.getItem(nodeStatsCacheKey);

          if (cached && cachedStats && cachedNodeStats) {
            const cacheData = JSON.parse(cached);
            const statsData = JSON.parse(cachedStats);
            const nodeStatsData = JSON.parse(cachedNodeStats);
            const cacheAge = Date.now() - cacheData.timestamp;

            // Use cache if less than 10 minutes old
            if (cacheAge < 10 * 60 * 1000) {
              setUnits(cacheData.units);
              setUnitStats(statsData.stats);
              setNodeStatuses(nodeStatsData.nodeStats);
              setInitialLoadComplete(true);
              return;
            }
          }
        } catch (error) { /* Ignore errors */ }
      }

      try {
        setLoading(true);
        setInitialLoadComplete(false);
        const response = await withTokenRefresh(apiClient.getStorageBuckets.bind(apiClient));

        if (isMounted && seq === loadSeqRef.current) {
          if (response?.buckets) {
            // Filter using the captured selection; if 'ALL' (or falsy), show all
            const filtered = (response.buckets as any[]).filter((unit: any) => {
              if (initialSelected === 'ALL') return true;
              const wid =
                unit.workspaceId ||
                unit.workspace_id ||
                (unit.workspace && (unit.workspace.id || unit.workspace)) ||
                unit.orgId ||
                unit.org_id;
              return String(wid) === String(initialSelected);
            });

            // Ignore stale result if workspace changed during the request
            if (selectedWorkspaceId !== initialSelected) {
              return;
            }

            setUnits(filtered);

            // Récupérer les statistiques une fois les unités chargées (guarded by seq)
            const unitIds = filtered.map((unit) => unit.id);
            statsSeqRef.current = seq;
            nodeStatsSeqRef.current = seq;

            // Load stats and node stats, then mark as complete
            if (unitIds.length > 0) {
              try {
                await Promise.all([fetchUnitStats(unitIds), fetchNodeStats(unitIds)]);
              } catch (error) { /* Ignore errors */ }
            }

            // Save to cache
            try {
              localStorage.setItem(
                cacheKey,
                JSON.stringify({
                  units: filtered,
                  timestamp: Date.now(),
                })
              );
            } catch (error) { /* Ignore errors */ }

            // Mark initial load as complete after everything is loaded
            if (isMounted && seq === loadSeqRef.current) {
              setInitialLoadComplete(true);
              setForceRefresh(false);
            }
          } else {
            setUnits([]);
            if (isMounted && seq === loadSeqRef.current) {
              setInitialLoadComplete(true);
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          setUnits([]);
        }
      } finally {
        if (isMounted && seq === loadSeqRef.current) {
          setLoading(false);
          // Ensure initial load is marked complete even on error
          setInitialLoadComplete(true);
        }
      }
    };

    loadStorageUnits();

    // Refresh stats every 5 minutes
    const statsRefreshInterval = setInterval(() => {
      const currentUnitIds = units.map((unit) => unit.id);
      if (currentUnitIds.length > 0) {
        fetchUnitStats(currentUnitIds);
        fetchNodeStats(currentUnitIds); // Actualiser également les statuts des nœuds
      }
    }, 300000); // 5 minutes

    return () => {
      isMounted = false;
      clearInterval(statsRefreshInterval);
    };
  }, [apiClient, isAuthenticated, navigate, withTokenRefresh, selectedWorkspaceId, forceRefresh]);

  // Event listener pour fermer les menus en cliquant ailleurs
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest('.dropdown-menu') &&
        !target.closest('button[aria-label="More options"]')
      ) {
        document.querySelectorAll('.dropdown-menu').forEach((menu) => {
          menu.classList.add('hidden');
        });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Helper function to detect provider name based on endpoint URL
  const detectProviderFromEndpoint = (endpoint: string): string | null => {
    if (!endpoint) return null;
    const url = endpoint.toLowerCase();
    
    // Known S3-compatible providers
    if (url.includes('minio')) return 'MinIO';
    if (url.includes('wasabi')) return 'Wasabi';
    if (url.includes('cloudflare') || url.includes('r2.')) return 'Cloudflare R2';
    if (url.includes('ovh') || url.includes('storage.gra') || url.includes('storage.sbg')) return 'OVH';
    if (url.includes('alibaba') || url.includes('aliyun') || url.includes('oss-')) return 'Alibaba Cloud';
    if (url.includes('digitalocean') || url.includes('spaces')) return 'DigitalOcean Spaces';
    if (url.includes('backblaze') || url.includes('b2')) return 'Backblaze B2';
    if (url.includes('akave')) return 'Akave';
    if (url.includes('storj')) return 'Storj';
    if (url.includes('nami')) return 'Nami Cloud';
    if (url.includes('vultr')) return 'Vultr';
    if (url.includes('linode') || url.includes('linodeobjects')) return 'Linode';
    if (url.includes('scaleway')) return 'Scaleway';
    if (url.includes('upcloud')) return 'UpCloud';
    if (url.includes('contabo')) return 'Contabo';
    
    // If endpoint is provided but not recognized, return generic
    return 'S3-Compatible';
  };

  const resetForm = () => {
    // Récupérer le type de stockage sélectionné du localStorage s'il existe
    const savedStorageType = localStorage.getItem('selectedStorageType');

    setNewUnit({
      name: '',
      storageType: savedStorageType
        ? (savedStorageType as ApiTypes.StorageType)
        : ApiTypes.StorageType.S3,
      apiKeyId: undefined,
    });
    setCompatibleProvider(ApiTypes.StorageType.S3); // Réinitialiser à S3 par défaut
    setS3Fields({
      bucket: '',
      accessKey: '',
      secret: '',
      region: '',
      endpoint: '',
    });
    setGcsFields({
      bucket: '',
      clientEmail: '',
      privateKey: '',
      endpoint: '',
    });
    setAzureFields({
      container: '',
      storageAccount: '',
      accessKey: '',
    });
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Fonction pour formater les bytes en format lisible (KB, MB, GB, etc.)
  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

    const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));

    // Gérer les valeurs négatives pour le changement de stockage
    const sign = bytes < 0 ? '-' : '';
    const formattedValue = parseFloat((Math.abs(bytes) / Math.pow(k, i)).toFixed(dm));

    return sign + formattedValue + ' ' + sizes[i];
  };

  // Format bytes to GB with max 3 decimals (for table columns down/up/change)
  const formatBytesToGB = (bytes: number): string => {
    if (bytes === 0) return '0 GB';
    const sign = bytes < 0 ? '-' : '';
    const absBytes = Math.abs(bytes);
    const gb = absBytes / (1024 * 1024 * 1024);
    // Max 3 decimals, remove trailing zeros
    const formatted = gb < 0.001 ? '0.001' : gb.toFixed(3).replace(/\.?0+$/, '');
    return `${sign}${formatted} GB`;
  };

  // Fonction pour générer des statistiques à zéro pour la démo
  // Cette fonction est déterministique pour un ID donné afin d'éviter les re-rendus constants
  const getRandomStats = (unitId?: string) => {
    return {
      calls: 0,
      storageChange: 0,
      downloadedBytes: 0,
      uploadedBytes: 0,
      isOnline: false,
      lastSeen: 0,
    };
  };

  // Définir l'interface pour les nœuds bridge
  interface BridgeNode {
    id: string;
    name: string;
    status: string;
    latency: number;
  }

  // Définir l'interface pour les statuts des nœuds
  interface NodeStatusData {
    nodes: BridgeNode[];
    statusCounts: { O: number; M: number; D: number };
  }

  // Fonction pour générer des données de nœuds avec valeurs à zéro (utilisée comme fallback)
  const generateNodeStatuses = (unitId: string) => {
    return {
      nodes: [],
      statusCounts: { O: 0, M: 0, D: 0 },
      noDataAvailable: true,
    };
  };

  // Fonction pour obtenir les statistiques d'une unité spécifique
  const getUnitStats = (unitId: string) => {
    const stats = unitStats[unitId] || getRandomStats(unitId);
    return stats;
  };

  const validateForm = (bEdit = false) => {
    const errors: typeof validationErrors = {};

    // Common validation
    if (!newUnit.name?.trim()) {
      errors.name = 'Name is required';
    }

    // Storage type specific validation
    const savedStorageType = localStorage.getItem('selectedStorageType');
    const isS3Compatible = savedStorageType === 'AKAVE' || savedStorageType === 'NAMI' || savedStorageType === 'OTHER';
    
    switch (newUnit.storageType) {
      case ApiTypes.StorageType.S3:
        if (!s3Fields.accessKey?.trim()) {
          errors.accessKey = 'Access Key is required';
        }
        if (!s3Fields.bucket?.trim()) {
          errors.bucket = 'Bucket is required';
        }
        // Endpoint required for S3-compatible providers (not native AWS)
        if (isS3Compatible && !s3Fields.endpoint?.trim()) {
          errors.endpoint = 'Endpoint is required for S3-compatible providers';
        }
        break;
      case ApiTypes.StorageType.GCS:
        if (!gcsFields.clientEmail?.trim()) {
          errors.clientEmail = 'Client Email is required';
        }
        if (!gcsFields.bucket?.trim()) {
          errors.bucket = 'Bucket is required';
        }
        break;
      case ApiTypes.StorageType.BLOB:
        if (!azureFields.storageAccount?.trim()) {
          errors.storageAccount = 'Storage Account is required';
        }
        if (!azureFields.container?.trim()) {
          errors.container = 'Container is required';
        }
        if (!bEdit && !azureFields.accessKey?.trim()) {
          errors.accessKey = 'Access Key is required';
        }
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getBucket = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3:
        return s3Fields.bucket;
      case ApiTypes.StorageType.GCS:
        return gcsFields.bucket;
      case ApiTypes.StorageType.BLOB:
        return azureFields.container;
    }
  };

  const getKey = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3:
        return s3Fields.accessKey;
      case ApiTypes.StorageType.GCS:
        return gcsFields.clientEmail;
      case ApiTypes.StorageType.BLOB:
        return azureFields.storageAccount;
    }
  };

  const getSecret = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3:
        return s3Fields.secret;
      case ApiTypes.StorageType.GCS:
        return gcsFields.privateKey;
      case ApiTypes.StorageType.BLOB:
        return azureFields.accessKey;
    }
  };

  const handleCreateUnit = async () => {
    // If we're not using an existing API key, validate all fields
    if (!newUnit.apiKeyId && !validateForm()) {
      return;
    }

    try {
      // Workspace is required and should not be 'ALL'
      if (!selectedWorkspaceId || selectedWorkspaceId === 'ALL') {
        toastError('Please select a specific workspace for this bucket.');
        return;
      }

      if (!newUnit.name?.trim()) {
        toastError('Name is required.');
        return;
      }

      // Créer la requête pour POST /bucket avec mapping BLOB->AZURE
      const mapType = (t: ApiTypes.StorageType | undefined) =>
        (t === ApiTypes.StorageType.BLOB ? 'AZURE' : t) as any;

      const createBucketRequest: any = {
        name: newUnit.name || '',
        bucket: getBucket(newUnit.storageType || ApiTypes.StorageType.S3),
        storageType: mapType(newUnit.storageType || ApiTypes.StorageType.S3),
        workspaceId: selectedWorkspaceId,
      };

      // If an existing API key is selected, use it
      if (newUnit.apiKeyId) {
        createBucketRequest.apiKeyUuid = newUnit.apiKeyId;
      } else {
        createBucketRequest.key = getKey(newUnit.storageType || ApiTypes.StorageType.S3) || '';
        createBucketRequest.secret = getSecret(newUnit.storageType || ApiTypes.StorageType.S3) || '';
        
        // Ajouter les champs optionnels seulement s'ils sont définis et non vides
        if (newUnit.storageType === ApiTypes.StorageType.S3) {
          if (s3Fields.region) createBucketRequest.region = s3Fields.region;
          if (s3Fields.endpoint) createBucketRequest.endpoint = s3Fields.endpoint;
        }
      }

      const response = await withTokenRefresh(() =>
        apiClient.createStorageBucket(createBucketRequest)
      );

      if (response.success) {
        // Invalider le cache pour forcer un refresh
        try {
          const cacheKey = `buckets_cache_${selectedWorkspaceId}`;
          const statsCacheKey = `buckets_stats_cache_${selectedWorkspaceId}`;
          const nodeStatsCacheKey = `buckets_node_stats_cache_${selectedWorkspaceId}`;
          localStorage.removeItem(cacheKey);
          localStorage.removeItem(statsCacheKey);
          localStorage.removeItem(nodeStatsCacheKey);
        } catch (error) { /* Ignore errors */ }

        // CRITICAL: Pass workspaceId to API call
        const workspaceIdParam = globalSelectedWorkspaceId && globalSelectedWorkspaceId !== 'ALL' ? globalSelectedWorkspaceId : undefined;
        const units = await withTokenRefresh(() => apiClient.getStorageBuckets(workspaceIdParam));
        setUnits(units.buckets);

        // Sauvegarder l'ID de la nouvelle unité dans sessionStorage
        const createdId = (response as any).bucketId || (response as any).unitId;
        if (createdId) {
          // Récupérer les IDs existants ou initialiser un tableau vide
          const existingBuckets = sessionStorage.getItem('newlyCreatedBuckets');
          const newBucketIds = existingBuckets ? JSON.parse(existingBuckets) : [];

          // Ajouter le nouvel ID
          newBucketIds.push(createdId);

          // Sauvegarder dans sessionStorage
          sessionStorage.setItem('newlyCreatedBuckets', JSON.stringify(newBucketIds));
          // Write to legacy key for compatibility
          try {
            const existingUnits = sessionStorage.getItem('newlyCreatedUnits');
            const legacyIds = existingUnits ? JSON.parse(existingUnits) : [];
            legacyIds.push(createdId);
            sessionStorage.setItem('newlyCreatedUnits', JSON.stringify(legacyIds));
          } catch (e) {
            /* ignore */
          }
        }

        setShowAddBucketForm(false);
        // Reset all fields
        resetForm();
        setValidationErrors({});
        toastBucketCreated();
        try {
          window.dispatchEvent(new Event('bucketsUpdated'));
        } catch (e) {
          /* ignore dispatch errors */
        }
      } else {
        toastErrorCustom('Failed to create bucket: ' + (response as ActionResponse).message);
      }
    } catch (error) {
      toastErrorCustom('Failed to create bucket: ' + error);
    }
  };

  const getWorkspaceIdToUse = () => {
    const saved = localStorage.getItem('selectedWorkspaceId') || 'ALL';
    const workspaceIdToUse = saved !== 'ALL' ? saved : selectedWorkspaceId;
    if (!workspaceIdToUse) {
      throw new Error('Please select a workspace');
    }
    return workspaceIdToUse;
  };

  const handleEditUnit = async () => {
    if (!validateForm(true)) {
      return;
    }

    try {
      // Get workspaceId from selectedUnit
      const workspaceIdToUse =
        (selectedUnit as any)?.workspaceId || (selectedUnit as any)?.workspace_id;
      if (!workspaceIdToUse) {
        throw new Error('Workspace ID is required to update unit');
      }

      const updateStorageUnit: ApiTypes.UpdateUnitRequest = {
        name: newUnit.name || '',
        bucket: getBucket(newUnit.storageType || ApiTypes.StorageType.S3),
        storageType: newUnit.storageType || ApiTypes.StorageType.S3,
        key: getKey(newUnit.storageType || ApiTypes.StorageType.S3),
        secret: getSecret(newUnit.storageType || ApiTypes.StorageType.S3),
        workspaceId: workspaceIdToUse,
        region: s3Fields.region || '',
        endpoint: s3Fields.endpoint || '',
      };

      const response = await withTokenRefresh(() =>
        apiClient.updateStorageBucket(selectedUnit?.id || '', updateStorageUnit as any)
      );

      if (response.success) {
        // Invalider le cache pour forcer un refresh
        try {
          const cacheKey = `buckets_cache_${selectedWorkspaceId}`;
          const statsCacheKey = `buckets_stats_cache_${selectedWorkspaceId}`;
          const nodeStatsCacheKey = `buckets_node_stats_cache_${selectedWorkspaceId}`;
          localStorage.removeItem(cacheKey);
          localStorage.removeItem(statsCacheKey);
          localStorage.removeItem(nodeStatsCacheKey);
        } catch (error) { /* Ignore errors */ }

        // CRITICAL: Pass workspaceId to API call
        const workspaceIdParam = globalSelectedWorkspaceId && globalSelectedWorkspaceId !== 'ALL' ? globalSelectedWorkspaceId : undefined;
        const units = await withTokenRefresh(() => apiClient.getStorageBuckets(workspaceIdParam));
        setUnits(units.buckets);
        setOpenEditUnit(false);
        setSelectedUnit(null);
        // Reset all fields
        resetForm();
        toastSuccess('Storage unit updated successfully');
        try {
          window.dispatchEvent(new Event('bucketsUpdated'));
        } catch (e) {
          /* ignore dispatch errors */
        }
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 404:
            toastError('Storage unit not found');
            break;
          case 400:
            toastError('Unit name must be unique');
            break;
          default:
            toastError(`Failed to update storage unit: ${error.message}`);
        }
      } else {
        toastError('Failed to update storage unit. Please try again.');
      }
    }
  };

  const handleDeleteUnit = async (unitId: string) => {
    try {
      const response = await withTokenRefresh(() => apiClient.deleteStorageBucket(unitId));

      if (response.success) {
        // Invalider le cache pour forcer un refresh
        try {
          const cacheKey = `buckets_cache_${selectedWorkspaceId}`;
          const statsCacheKey = `buckets_stats_cache_${selectedWorkspaceId}`;
          const nodeStatsCacheKey = `buckets_node_stats_cache_${selectedWorkspaceId}`;
          localStorage.removeItem(cacheKey);
          localStorage.removeItem(statsCacheKey);
          localStorage.removeItem(nodeStatsCacheKey);
        } catch (error) { /* Ignore errors */ }

        // CRITICAL: Pass workspaceId to API call
        const workspaceIdParam = globalSelectedWorkspaceId && globalSelectedWorkspaceId !== 'ALL' ? globalSelectedWorkspaceId : undefined;
        const units = await withTokenRefresh(() => apiClient.getStorageBuckets(workspaceIdParam));
        setUnits(units.buckets);
        setSelectedUnit(null);
        toastSuccess('Storage unit deleted successfully');
        try {
          window.dispatchEvent(new Event('bucketsUpdated'));
        } catch (e) {
          /* ignore dispatch errors */
        }
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 404:
            toastError('Storage unit not found');
            break;
          case 403:
            toastError(
              'Storage unit is being used by a repo, please remove it from the repo first'
            );
            break;
          default:
            toastError(`Failed to delete storage unit: ${error.message}`);
        }
      } else {
        toastError('Failed to delete storage unit. Please try again.');
      }
    }
  };

  // Global loader during initial data fetch (but not during add/edit operations)
  if (loading && !initialLoadComplete && !showAddBucketForm && !showProviderSelection) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ minHeight: 'calc(100vh - 200px)' }}
      >
        <div
          className="animate-spin rounded-full h-16 w-16 border-b-2 mb-4"
          style={{ borderColor: '#617BFF' }}
        ></div>
        <p className="text-gray-600 text-lg font-medium">Loading buckets...</p>
        <p className="text-gray-500 text-sm mt-2">Fetching data and statistics</p>
        <p className="text-gray-400 text-xs mt-3 max-w-md text-center">
          First load may take a few moments. Next visits will be instant thanks to caching.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          {(showProviderSelection || showAddBucketForm) && (
            <Button
              variant="text"
              startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>}
              onClick={() => {
                // Progressive back navigation
                if (showAddBucketForm) {
                  // If on form, go back to provider selection
                  setShowAddBucketForm(false);
                  setShowProviderSelection(true);
                  resetForm();
                  setValidationErrors({});
                } else if (showProviderSelection) {
                  // If on provider selection, go back to bucket list
                  setShowProviderSelection(false);
                }
              }}
              sx={{ mb: 2, color: '#617BFF', textTransform: 'none', fontWeight: 500, border: '1px solid #617BFF', borderRadius: '8px', px: 2, '&:hover': { bgcolor: '#F5F7FF' } }}
            >
              Back to Buckets
            </Button>
          )}
          <Typography
            style={{
              fontSize: '32px',
              fontWeight: 700,
              color: '#1F2937',
              marginBottom: '8px',
              fontFamily: 'Inter',
              letterSpacing: '-0.02em',
            }}
          >
            {showProviderSelection || showAddBucketForm ? 'Add Bucket' : 'Cloud Storage'}
          </Typography>
          <Typography
            style={{
              fontSize: '16px',
              fontWeight: 400,
              color: '#6B7280',
              fontFamily: 'Inter',
              lineHeight: '24px',
            }}
          >
            {showProviderSelection || showAddBucketForm 
              ? 'Follow the simple steps below to create a bucket'
              : 'Manage your cloud storage buckets and repositories'}
          </Typography>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '0 40px 40px 40px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#FFFFFF' }}>
          <div className="flex-1 overflow-y-auto p-6">

      {/* Buckets Sub-section Header with buttons */}
      {!showProviderSelection && !showAddBucketForm && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
                Buckets
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                Manage your cloud storage buckets and their configurations.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Search */}
              {showSearch ? (
                <TextField
                  size="small"
                  placeholder="Search buckets..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  onBlur={() => { if (!searchQuery) setShowSearch(false); }}
                  autoFocus
                  sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '36px' } }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ fontSize: 18, color: '#667085', mr: 0.5 }} />,
                  }}
                />
              ) : (
                <Tooltip title="Search">
                  <IconButton
                    size="small"
                    onClick={() => setShowSearch(true)}
                    sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                  >
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              {/* Reload */}
              <Tooltip title="Reload">
                <IconButton
                  size="small"
                  onClick={() => setForceRefresh(true)}
                  sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              {/* Add */}
              <Tooltip title="Add bucket">
                <IconButton
                  size="small"
                  onClick={() => setShowProviderSelection(true)}
                  sx={{ width: 36, height: 36, bgcolor: '#617BFF', borderRadius: '8px', color: 'white', '&:hover': { bgcolor: '#4A67E3' } }}
                >
                  <AddIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      )}

      {showProviderSelection ? (
        <div className="mt-4">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            {/* Workspace selection row above header */}
            <div className="flex justify-end mb-3">
              <Box sx={{ minWidth: 320, width: 420 }}>
                {globalWorkspaceAll ||
                (workspaceOverride && initialGlobalWorkspaceAll) ||
                selectedWorkspaceId === '' || selectedWorkspaceId === 'ALL' ? (
                  <FormControl fullWidth size="small">
                    <InputLabel id="workspace-select-label" shrink>
                      Workspace
                    </InputLabel>
                    <Select
                      labelId="workspace-select-label"
                      label="Workspace"
                      displayEmpty
                      value={selectedWorkspaceId === 'ALL' ? '' : selectedWorkspaceId}
                      onChange={(e) => {
                        const val = e.target.value as string;
                        setSelectedWorkspaceId(val);
                        // Also update global selection
                        setSelectedWorkspace(val);
                      }}
                      renderValue={(val) => {
                        if (!val) {
                          return <span style={{ color: '#9CA3AF' }}>Choose a workspace</span>;
                        }
                        const found = workspaces.find((w) => w.id === val);
                        return found ? (
                          <span style={{ color: '#111827' }}>{found.name}</span>
                        ) : (
                          <span style={{ color: '#9CA3AF' }}>Choose a workspace</span>
                        );
                      }}
                      sx={{
                        '& .MuiSelect-select': {
                          py: '8px',
                        },
                      }}
                    >
                      {!selectedWorkspaceId && (
                        <MenuItem value="" disabled>
                          Choose a workspace
                        </MenuItem>
                      )}
                      {workspaces.map((w) => (
                        <MenuItem key={w.id} value={w.id}>
                          {w.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {!globalWorkspaceAll && (
                      <Typography
                        variant="caption"
                        sx={{ mt: 0.5, color: '#6B7280', display: 'block' }}
                      >
                        This selection is local to bucket creation and does not change your global
                        workspace.
                      </Typography>
                    )}
                  </FormControl>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 1.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                      Workspace
                    </Typography>
                    <Chip
                      label={workspaces.find((w) => w.id === selectedWorkspaceId)?.name || '—'}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '12px',
                        fontWeight: 600,
                        borderRadius: '999px',
                        bgcolor: '#FAFAFA',
                        border: '1px solid #E9EAEB',
                        color: '#414651',
                      }}
                    />
                    {initialGlobalWorkspaceAll && (
                      <StyledButton
                        text="Change"
                        onClick={() => setWorkspaceOverride(true)}
                        variant="dark"
                      />
                    )}
                  </Box>
                )}
              </Box>
            </div>

            <div className="flex justify-between items-center mb-6">
              <Box>
                <Typography
                  variant="h5"
                  className="text-black"
                  style={{ fontWeight: 600, fontSize: '20px', lineHeight: '30px' }}
                >
                  Select Provider
                </Typography>
                <Typography
                  variant="body1"
                  style={{
                    color: '#535862',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    marginTop: '4px',
                  }}
                >
                  Start by selecting a provider to configure your bucket.
                </Typography>
              </Box>
              {!showHelp && (
                <IconButton
                  size="small"
                  onClick={() => setShowHelp((v) => !v)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    width: 28,
                    height: 28,
                  }}
                  aria-label="Help and Support"
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              )}
            </div>
            <div className="flex flex-col-reverse lg:flex-row gap-4">
              <div
                className={`grid grid-cols-1 ${
                  showHelp ? 'lg:grid-cols-1 xl:grid-cols-2' : 'sm:grid-cols-2 xl:grid-cols-3'
                } gap-4 sm:gap-6 flex-1`}
              >
                {/* Google Cloud Provider */}
                <div
                  className="border border-gray-200 hover:border-blue-500 transition-all rounded-lg"
                  style={{
                    width: '100%',
                    height: 205,
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div className="flex-1 p-4">
                    <div className="flex items-center mb-3">
                      <img src={logoGoogle} alt="Google Cloud Storage" className="w-12 h-12 mr-2" />
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 16,
                          lineHeight: '24px',
                          color: '#181D27',
                        }}
                      >
                        Google Cloud
                      </span>
                    </div>
                    <p
                      style={{
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#535862',
                      }}
                    >
                      Highly scalable cloud storage solution with automatic replication and global
                      access to your data.
                    </p>
                  </div>
                  <div
                    className="flex items-center justify-end border-t px-4"
                    style={{ height: 53 }}
                  >
                    <button
                      className="hover:text-blue-800"
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#4766FF',
                      }}
                      onClick={() => {
                        localStorage.setItem('selectedStorageType', ApiTypes.StorageType.GCS);
                        setNewUnit((prev) => ({ ...prev, storageType: ApiTypes.StorageType.GCS }));
                        setShowProviderSelection(false);
                        setShowAddBucketForm(true);
                      }}
                    >
                      Select provider
                    </button>
                  </div>
                </div>

                {/* AWS Provider */}
                <div
                  className="border border-gray-200 hover:border-blue-500 transition-all rounded-lg"
                  style={{
                    width: '100%',
                    height: 205,
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div className="flex-1 p-4">
                    <div className="flex items-center mb-3">
                      <img src={logoAws} alt="AWS" className="w-12 h-12 mr-2" />
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 16,
                          lineHeight: '24px',
                          color: '#181D27',
                        }}
                      >
                        AWS
                      </span>
                    </div>
                    <p
                      style={{
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#535862',
                      }}
                    >
                      Secure and scalable cloud infrastructure with S3, offering durability,
                      availability and high performance.
                    </p>
                  </div>
                  <div
                    className="flex items-center justify-end border-t px-4"
                    style={{ height: 53 }}
                  >
                    <button
                      className="hover:text-blue-800"
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#4766FF',
                      }}
                      onClick={() => {
                        localStorage.setItem('selectedStorageType', ApiTypes.StorageType.S3);
                        setNewUnit((prev) => ({ ...prev, storageType: ApiTypes.StorageType.S3 }));
                        setShowProviderSelection(false);
                        setShowAddBucketForm(true);
                      }}
                    >
                      Select provider
                    </button>
                  </div>
                </div>

                {/* Azure Provider */}
                <div
                  className="border border-gray-200 hover:border-blue-500 transition-all rounded-lg"
                  style={{
                    width: '100%',
                    height: 205,
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div className="flex-1 p-4">
                    <div className="flex items-center mb-3">
                      <img src={logoAzure} alt="Azure" className="w-12 h-12 mr-2" />
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 16,
                          lineHeight: '24px',
                          color: '#181D27',
                        }}
                      >
                        Azure
                      </span>
                    </div>
                    <p
                      style={{
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#535862',
                      }}
                    >
                      Enterprise-grade cloud storage with strong security, compliance features and
                      global redundancy.
                    </p>
                  </div>
                  <div
                    className="flex items-center justify-end border-t px-4"
                    style={{ height: 53 }}
                  >
                    <button
                      className="hover:text-blue-800"
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#4766FF',
                      }}
                      onClick={() => {
                        localStorage.setItem('selectedStorageType', ApiTypes.StorageType.BLOB);
                        setNewUnit((prev) => ({ ...prev, storageType: ApiTypes.StorageType.BLOB }));
                        setShowProviderSelection(false);
                        setShowAddBucketForm(true);
                      }}
                    >
                      Select provider
                    </button>
                  </div>
                </div>

                {/* Akave Provider */}
                <div
                  className="border border-gray-200 hover:border-blue-500 transition-all rounded-lg"
                  style={{
                    width: '100%',
                    height: 205,
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div className="flex-1 p-4">
                    <div className="flex items-center mb-3">
                      <img src={logoAkave} alt="Akave" className="w-12 h-12 mr-2" />
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 16,
                          lineHeight: '24px',
                          color: '#181D27',
                        }}
                      >
                        Akave
                      </span>
                    </div>
                    <p
                      style={{
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#535862',
                      }}
                    >
                      Decentralized storage with cryptographic proof, offering censorship resistance
                      and cost reduction through an open market.
                    </p>
                  </div>
                  <div
                    className="flex items-center justify-end border-t px-4"
                    style={{ height: 53 }}
                  >
                    <button
                      className="hover:text-blue-800"
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#4766FF',
                      }}
                      onClick={() => {
                        localStorage.setItem('selectedStorageType', 'AKAVE');
                        setNewUnit((prev) => ({ ...prev, storageType: ApiTypes.StorageType.S3 }));
                        setShowProviderSelection(false);
                        setShowAddBucketForm(true);
                      }}
                    >
                      Select provider
                    </button>
                  </div>
                </div>

                {/* Storj Provider */}
                <div
                  className="border border-gray-200 hover:border-blue-500 transition-all rounded-lg"
                  style={{
                    width: '100%',
                    height: 205,
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div className="flex-1 p-4">
                    <div className="flex items-center mb-3">
                      <img src={logoStorj} alt="Storj" className="w-12 h-12 mr-2" />
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 16,
                          lineHeight: '24px',
                          color: '#181D27',
                        }}
                      >
                        Storj
                      </span>
                    </div>
                    <p
                      style={{
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#535862',
                      }}
                    >
                      Decentralized cloud storage platform offering security, privacy, and
                      reliability at a lower cost than traditional solutions.
                    </p>
                  </div>
                  <div
                    className="flex items-center justify-end border-t px-4"
                    style={{ height: 53 }}
                  >
                    <button
                      className="hover:text-blue-800"
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#4766FF',
                      }}
                      onClick={() => {
                        localStorage.setItem('selectedStorageType', ApiTypes.StorageType.S3);
                        setNewUnit((prev) => ({ ...prev, storageType: ApiTypes.StorageType.S3 }));
                        setShowProviderSelection(false);
                        setShowAddBucketForm(true);
                      }}
                    >
                      Select provider
                    </button>
                  </div>
                </div>

                {/* Nami Cloud Provider */}
                <div
                  className="border border-gray-200 hover:border-blue-500 transition-all rounded-lg"
                  style={{
                    width: '100%',
                    height: 205,
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div className="flex-1 p-4">
                    <div className="flex items-center mb-3">
                      <img src={logoNami} alt="Nami Cloud" className="w-12 h-12 mr-2" />
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 16,
                          lineHeight: '24px',
                          color: '#181D27',
                        }}
                      >
                        Nami Cloud
                      </span>
                    </div>
                    <p
                      style={{
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#535862',
                      }}
                    >
                      Built for DePIN, Nami is a decentralized storage platform offering security,
                      privacy, and reliability on top of Walrus.
                    </p>
                  </div>
                  <div
                    className="flex items-center justify-end border-t px-4"
                    style={{ height: 53 }}
                  >
                    <button
                      className="hover:text-blue-800"
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#4766FF',
                      }}
                      onClick={() => {
                        localStorage.setItem('selectedStorageType', 'NAMI');
                        setNewUnit((prev) => ({ ...prev, storageType: ApiTypes.StorageType.S3 }));
                        setShowProviderSelection(false);
                        setShowAddBucketForm(true);
                      }}
                    >
                      Select provider
                    </button>
                  </div>
                </div>

                {/* Other Provider */}
                <div 
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition-all"
                  style={{
                    width: '100%',
                  }}
                >
                  <div className="flex items-center mb-3">
                    <img src={logoOther} alt="Other" className="w-12 h-12 mr-2" />
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        lineHeight: '24px',
                        color: '#181D27',
                      }}
                    >
                      Other
                    </span>
                  </div>
                  <p
                    style={{ fontWeight: 400, fontSize: 14, lineHeight: '20px', color: '#535862' }}
                    className="mb-4"
                  >
                    Connect to any storage provider not listed above. You'll be able to choose
                    between S3, Google Cloud Storage, or Azure Blob.
                  </p>
                  <div className="text-right">
                    <button
                      className="hover:text-blue-800"
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: '20px',
                        color: '#4766FF',
                      }}
                      onClick={() => {
                        localStorage.setItem('selectedStorageType', 'OTHER');
                        setNewUnit((prev) => ({ ...prev, storageType: ApiTypes.StorageType.S3 }));
                        setCompatibleProvider(ApiTypes.StorageType.S3); // Initialiser à S3 par défaut
                        setShowProviderSelection(false);
                        setShowAddBucketForm(true);
                      }}
                    >
                      Select provider
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {showHelp && (
                <div
                  className="w-full lg:w-auto lg:max-w-[343px]"
                  style={{
                    border: '1px solid var(--mui-palette-divider, #E5E7EB)',
                    borderRadius: 12,
                    background: '#FAFAFA',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 0,
                  }}
                >
                    <div
                      className="flex items-center justify-between"
                      style={{
                        height: 70,
                        padding: '0 16px',
                        background: '#FAFAFA',
                        borderBottom: '1px solid var(--mui-palette-divider, #E5E7EB)',
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                      }}
                    >
                      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                        Help and Support
                      </Typography>
                      <IconButton size="small" onClick={() => setShowHelp(false)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </div>
                    <div
                      className="flex flex-col"
                      style={{ gap: 12, padding: '0 16px 16px 16px', marginTop: 12 }}
                    >
                      {/* What's a Bucket in Flashgate? */}
                      <div
                        onClick={() => setShowHelp(false)}
                        style={{
                          width: '100%',
                          padding: '16px',
                          borderRadius: 12,
                          border: '1px solid #E5E7EB',
                          background: '#FFFFFF',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          textDecoration: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#617BFF';
                          e.currentTarget.style.background = '#F9FAFB';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#E5E7EB';
                          e.currentTarget.style.background = '#FFFFFF';
                        }}
                      >
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            background: '#F0F2FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M13 5h4m0 0v4m0-4L9 13"
                              stroke="#617BFF"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#181D27' }}>
                            What's a Bucket in Flashgate?
                          </div>
                          <div style={{ fontWeight: 400, fontSize: 13, lineHeight: '18px', color: '#717680', marginTop: 2 }}>
                            Learn about buckets concepts
                          </div>
                        </div>
                      </div>

                      {/* Create a Bucket? */}
                      <a
                        href="https://docs.flashback.tech/guides/create-a-bucket"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          width: '100%',
                          padding: '16px',
                          borderRadius: 12,
                          border: '1px solid #E5E7EB',
                          background: '#FFFFFF',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          textDecoration: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#617BFF';
                          e.currentTarget.style.background = '#F9FAFB';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#E5E7EB';
                          e.currentTarget.style.background = '#FFFFFF';
                        }}
                      >
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            background: '#F0F2FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                              d="M10 5v10m-5-5h10"
                              stroke="#617BFF"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#181D27' }}>
                            Create a Bucket
                          </div>
                          <div style={{ fontWeight: 400, fontSize: 13, lineHeight: '18px', color: '#717680', marginTop: 2 }}>
                            Follow the step-by-step guide
                          </div>
                        </div>
                      </a>

                      {/* Build with API */}
                      <a
                        href="https://docs.flashback.tech/support-reference/platform-api-reference/bucket-management"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          width: '100%',
                          padding: '16px',
                          borderRadius: 12,
                          border: '1px solid #E5E7EB',
                          background: '#FFFFFF',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          textDecoration: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#617BFF';
                          e.currentTarget.style.background = '#F9FAFB';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#E5E7EB';
                          e.currentTarget.style.background = '#FFFFFF';
                        }}
                      >
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            background: '#F0F2FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                              d="M6 8l-2 2 2 2m8-4l2 2-2 2M11 5L9 15"
                              stroke="#617BFF"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#181D27' }}>
                            Build with API
                          </div>
                          <div style={{ fontWeight: 400, fontSize: 13, lineHeight: '18px', color: '#717680', marginTop: 2 }}>
                            Explore bucket API reference
                          </div>
                        </div>
                      </a>
                    </div>
                </div>
              )}
            </div>
          </div>
      ) : showAddBucketForm ? (
        <div className="mt-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-100 p-6">
              <div className="flex justify-between items-center">
                <div>
                  <Typography variant="h6" className="text-gray-900 font-medium">
                    Add Bucket
                  </Typography>
                  <Typography variant="body2" className="text-gray-600 mt-1">
                    Configure the details of your new storage bucket.
                  </Typography>
                </div>
                {/* Bouton Back to List retiré sur demande */}
              </div>
            </div>

            <div className="p-6">
              {/* Selected Provider banner with change action */}
              <div className="mb-6 flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                <div className="flex items-center gap-3">
                  {(() => {
                    const savedStorageType = localStorage.getItem('selectedStorageType');
                    if (savedStorageType === 'AKAVE') {
                      return <img src={logoAkave} alt="Akave" className="w-6 h-6" />;
                    } else if (savedStorageType === 'NAMI') {
                      return <img src={logoNami} alt="Nami Cloud" className="w-6 h-6" />;
                    } else if (savedStorageType === 'OTHER') {
                      return null; // Pas d'icône pour Other
                    } else if (newUnit.storageType === ApiTypes.StorageType.S3) {
                      return <img src={logoAws} alt="AWS" className="w-6 h-6" />;
                    } else if (newUnit.storageType === ApiTypes.StorageType.GCS) {
                      return <img src={logoGoogle} alt="Google Cloud" className="w-6 h-6" />;
                    } else if (newUnit.storageType === ApiTypes.StorageType.BLOB) {
                      return <img src={logoAzure} alt="Azure" className="w-6 h-6" />;
                    }
                    return null;
                  })()}
                  <span className="text-sm text-gray-800">
                    Selected provider:
                    <span className="ml-1 font-semibold">
                      {(() => {
                        const savedStorageType = localStorage.getItem('selectedStorageType');
                        if (savedStorageType === 'AKAVE') return 'Akave';
                        if (savedStorageType === 'NAMI') return 'Nami Cloud';
                        if (savedStorageType === 'OTHER') return 'Other';
                        if (newUnit.storageType === ApiTypes.StorageType.S3) return 'AWS S3';
                        if (newUnit.storageType === ApiTypes.StorageType.GCS) return 'Google Cloud Storage';
                        if (newUnit.storageType === ApiTypes.StorageType.BLOB) return 'Azure Blob Storage';
                        return '';
                      })()}
                    </span>
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddBucketForm(false);
                    setShowProviderSelection(true);
                  }}
                  className="text-sm font-semibold"
                  style={{ color: '#4766FF' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = '#2E4EE8';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = '#4766FF';
                  }}
                >
                  Change provider
                </button>
              </div>
              {/* Bucket details section styled like the image */}
              <div className="mb-6">
                <Typography variant="subtitle1" className="text-gray-900 font-semibold mb-2">
                  Bucket details
                </Typography>
                <Typography variant="body2" className="text-gray-600 mb-4">
                  Next fill in the details for the bucket
                </Typography>

                {/* Standard dropdown for Other provider */}
                {isOtherProvider && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Standard <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={compatibleProvider}
                      onChange={(e) => {
                        const selectedType = e.target.value as ApiTypes.StorageType;
                        setCompatibleProvider(selectedType);
                        setNewUnit((prev) => ({ ...prev, storageType: selectedType }));
                        // Reset fields when changing standard
                        setS3Fields({ bucket: '', accessKey: '', secret: '', endpoint: '', region: '' });
                        setValidationErrors({});
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value={ApiTypes.StorageType.S3}>S3 (Minio, Wasabi, Cloudflare, OVH, Alibaba)</option>
                    </select>
                    <p className="mt-2 text-sm text-gray-700">
                      S3-compatible storage providers use the same API as Amazon S3.
                    </p>
                    {/* Show detected provider based on endpoint */}
                    {s3Fields.endpoint && detectProviderFromEndpoint(s3Fields.endpoint) && (
                      <div className="mt-3 flex items-center gap-2 text-sm">
                        <span className="text-gray-600">Detected provider:</span>
                        <span className="font-semibold text-blue-700">{detectProviderFromEndpoint(s3Fields.endpoint)}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Bucket Label */}
                  <div>
                    <label
                      htmlFor="bucket-name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      <span className="inline-flex items-center gap-1">
                        Bucket Label <span className="text-red-500">*</span>
                        <Tooltip
                          title="Human-readable label for this bucket in Flashgate (e.g. 'Backups-EU'). We recommend having a unique name per bucket to avoid issues when setting up your repository."
                          arrow
                          placement="top"
                        >
                          <IconButton size="small" sx={{ padding: 0, marginLeft: 0.5 }}>
                            <HelpOutlineIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                          </IconButton>
                        </Tooltip>
                      </span>
                    </label>
                    <input
                      type="text"
                      id="bucket-name"
                      placeholder="My Bucket Label"
                      value={newUnit.name || ''}
                      onChange={(e) => {
                        setNewUnit((prev) => ({ ...prev, name: e.target.value }));
                        setValidationErrors((prev) => ({ ...prev, name: undefined }));
                      }}
                      className={`w-full px-3 py-2 border ${
                        validationErrors.name ? 'border-red-500' : 'border-gray-300'
                      } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    />
                    {validationErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                    )}
                  </div>

                  {/* Bucket Name / Container (for Azure) */}
                  <div>
                    <label
                      htmlFor="bucket-id"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      <span className="inline-flex items-center gap-1">
                        {newUnit.storageType === ApiTypes.StorageType.BLOB ? 'Container' : 'Bucket'}{' '}
                        <span className="text-red-500">*</span>
                        <Tooltip
                          title={
                            newUnit.storageType === ApiTypes.StorageType.BLOB
                              ? 'The container within your Azure storage account.'
                              : 'The exact identifier as defined by your provider in your vendor tenant (e.g. the S3 bucket name in your AWS account or GCS bucket name in your Google Cloud account).'
                          }
                          arrow
                          placement="top"
                        >
                          <IconButton size="small" sx={{ padding: 0, marginLeft: 0.5 }}>
                            <HelpOutlineIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                          </IconButton>
                        </Tooltip>
                      </span>
                    </label>
                    <input
                      type="text"
                      id="bucket-id"
                      placeholder={
                        newUnit.storageType === ApiTypes.StorageType.BLOB
                          ? 'Container name'
                          : 'Bucket name'
                      }
                      value={getBucket(newUnit.storageType || ApiTypes.StorageType.S3)}
                      onChange={(e) => {
                        const field = 'bucket';
                        const value = e.target.value;
                        switch (newUnit.storageType) {
                          case ApiTypes.StorageType.S3:
                            setS3Fields((prev) => ({ ...prev, [field]: value }));
                            break;
                          case ApiTypes.StorageType.GCS:
                            setGcsFields((prev) => ({ ...prev, [field]: value }));
                            break;
                          case ApiTypes.StorageType.BLOB:
                            setAzureFields((prev) => ({ ...prev, container: value }));
                            break;
                        }
                        setValidationErrors((prev) => ({ ...prev, bucket: undefined }));
                      }}
                      className={`w-full px-3 py-2 border ${
                        validationErrors.bucket ? 'border-red-500' : 'border-gray-300'
                      } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    />
                    {validationErrors.bucket && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.bucket}</p>
                    )}
                  </div>

                  {/* Access key / Client Email / Access Key (optional for Azure) */}
                  <div>
                    <label
                      htmlFor="access-key"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      <span className="inline-flex items-center gap-1">
                        {newUnit.storageType === ApiTypes.StorageType.GCS
                          ? 'Client Email'
                          : newUnit.storageType === ApiTypes.StorageType.BLOB
                          ? 'Access Key (optional)'
                          : 'Access Key'}
                        {newUnit.storageType !== ApiTypes.StorageType.BLOB && (
                          <span className="text-red-500">*</span>
                        )}
                        <Tooltip
                          title={
                            newUnit.storageType === ApiTypes.StorageType.GCS
                              ? 'Your service account email OR a delegated service account.'
                              : newUnit.storageType === ApiTypes.StorageType.BLOB
                              ? 'Account key OR leave blank for delegated guest access.'
                              : 'Your S3 API Key/AWS access key ID OR the IAM Role ARN if using AWS delegated Role ARN.'
                          }
                          arrow
                          placement="top"
                        >
                          <IconButton size="small" sx={{ padding: 0, marginLeft: 0.5 }}>
                            <HelpOutlineIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                          </IconButton>
                        </Tooltip>
                      </span>
                    </label>
                    <input
                      type="text"
                      id="access-key"
                      placeholder={
                        newUnit.storageType === ApiTypes.StorageType.GCS
                          ? 'service-account@project.iam.gserviceaccount.com'
                          : newUnit.storageType === ApiTypes.StorageType.BLOB
                          ? 'Leave blank for delegated access'
                          : 'AKIAXXXXXXXXXXXXXXXX'
                      }
                      value={getKey(newUnit.storageType || ApiTypes.StorageType.S3)}
                      onChange={(e) => {
                        const field =
                          newUnit.storageType === ApiTypes.StorageType.GCS
                            ? 'clientEmail'
                            : 'accessKey';
                        const value = e.target.value;
                        switch (newUnit.storageType) {
                          case ApiTypes.StorageType.S3:
                            setS3Fields((prev) => ({ ...prev, [field]: value }));
                            break;
                          case ApiTypes.StorageType.GCS:
                            setGcsFields((prev) => ({ ...prev, [field]: value }));
                            break;
                          case ApiTypes.StorageType.BLOB:
                            setAzureFields((prev) => ({ ...prev, [field]: value }));
                            break;
                        }
                        setValidationErrors((prev) => ({ ...prev, key: undefined }));
                      }}
                      className={`w-full px-3 py-2 border ${
                        validationErrors.key ? 'border-red-500' : 'border-gray-300'
                      } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    />
                    {validationErrors.key && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.key}</p>
                    )}
                    {newUnit.storageType === ApiTypes.StorageType.S3 && (
                      <p className="mt-1 text-xs text-gray-500">
                        AWS access key ID or IAM Role ARN for delegated access
                      </p>
                    )}
                    {newUnit.storageType === ApiTypes.StorageType.GCS && (
                      <p className="mt-1 text-xs text-gray-500">
                        Service account email or delegated service account
                      </p>
                    )}
                  </div>

                  {/* Secret key / Private Key - hide for Azure */}
                  {newUnit.storageType !== ApiTypes.StorageType.BLOB && (
                    <div>
                      <label
                        htmlFor="secret-key"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        <span className="inline-flex items-center gap-1">
                          {newUnit.storageType === ApiTypes.StorageType.GCS
                            ? 'Private Key'
                            : 'Secret Key'}{' '}
                          <span className="text-red-500">*</span>
                          <Tooltip
                            title={
                              newUnit.storageType === ApiTypes.StorageType.GCS
                                ? 'Service account private key OR leave blank if using delegated access.'
                                : 'Your AWS secret key OR External ID for delegated roles.'
                            }
                            arrow
                            placement="top"
                          >
                            <IconButton size="small" sx={{ padding: 0, marginLeft: 0.5 }}>
                              <HelpOutlineIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                            </IconButton>
                          </Tooltip>
                        </span>
                      </label>
                      <input
                        type="password"
                        id="secret-key"
                        placeholder={
                          newUnit.storageType === ApiTypes.StorageType.GCS
                            ? '-----BEGIN PRIVATE KEY-----'
                            : 'Add the secret key'
                        }
                        value={getSecret(newUnit.storageType || ApiTypes.StorageType.S3)}
                        onChange={(e) => {
                          const field =
                            newUnit.storageType === ApiTypes.StorageType.GCS
                              ? 'privateKey'
                              : 'secret';
                          const value = e.target.value;
                          switch (newUnit.storageType) {
                            case ApiTypes.StorageType.S3:
                              setS3Fields((prev) => ({ ...prev, [field]: value }));
                              break;
                            case ApiTypes.StorageType.GCS:
                              setGcsFields((prev) => ({ ...prev, [field]: value }));
                              break;
                            case ApiTypes.StorageType.BLOB:
                              // Azure doesn't use a secret field in the same way
                              break;
                          }
                          setValidationErrors((prev) => ({ ...prev, secret: undefined }));
                        }}
                        className={`w-full px-3 py-2 border ${
                          validationErrors.secret ? 'border-red-500' : 'border-gray-300'
                        } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                      />
                      {validationErrors.secret && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.secret}</p>
                      )}
                      {newUnit.storageType === ApiTypes.StorageType.S3 && (
                        <p className="mt-1 text-xs text-gray-500">
                          AWS secret key or External ID for delegated roles
                        </p>
                      )}
                      {newUnit.storageType === ApiTypes.StorageType.GCS && (
                        <p className="mt-1 text-xs text-gray-500">
                          Service account private key or leave blank for delegated access
                        </p>
                      )}
                    </div>
                  )}

                  {/* Endpoint - for S3 (Akave/Nami/Other only) */}
                  {(() => {
                    const savedStorageType = localStorage.getItem('selectedStorageType');
                    const isAkave = savedStorageType === 'AKAVE';
                    const isNami = savedStorageType === 'NAMI';
                    const isOther = savedStorageType === 'OTHER';
                    const showEndpoint = newUnit.storageType === ApiTypes.StorageType.S3 && (isAkave || isNami || isOther);
                    
                    if (!showEndpoint) return null;
                    
                    return (
                      <div>
                        <label
                          htmlFor="endpoint"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Endpoint {(isAkave || isNami) && <span className="text-red-500">*</span>}
                          {!isAkave && !isNami && ' (optional)'}
                        </label>
                        <input
                          type="text"
                          id="endpoint"
                          placeholder={
                            newUnit.storageType === ApiTypes.StorageType.S3
                              ? 'Enter your endpoint URL'
                              : 'Leave empty for GCP'
                          }
                          value={
                            newUnit.storageType === ApiTypes.StorageType.S3
                              ? s3Fields.endpoint
                              : gcsFields.endpoint
                          }
                          onChange={(e) => {
                            if (newUnit.storageType === ApiTypes.StorageType.S3) {
                              setS3Fields((prev) => ({ ...prev, endpoint: e.target.value }));
                            } else {
                              setGcsFields((prev) => ({ ...prev, endpoint: e.target.value }));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          {isAkave
                            ? 'We support compatible endpoint for Akave, please use your endpoint URL as above'
                            : isNami
                            ? 'We support compatible endpoint for Nami Cloud, please use your endpoint URL as above'
                            : newUnit.storageType === ApiTypes.StorageType.S3
                            ? 'Custom S3-compatible endpoint URL'
                            : 'Custom GCS-compatible endpoint URL or leave blank for GCP'}
                        </p>
                      </div>
                    );
                  })()}

                  {/* Region - only for S3 (required if pure AWS, optional for Akave/Nami/Other) */}
                  {newUnit.storageType === ApiTypes.StorageType.S3 && (() => {
                    const savedStorageType = localStorage.getItem('selectedStorageType');
                    const isAkave = savedStorageType === 'AKAVE';
                    const isNami = savedStorageType === 'NAMI';
                    const isOther = savedStorageType === 'OTHER';
                    const isPureAWS = !isAkave && !isNami && !isOther;
                    
                    return (
                      <div>
                        <label
                          htmlFor="region"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Region {isPureAWS && !s3Fields.endpoint && <span className="text-red-500">*</span>}
                          {(isAkave || isNami || isOther) && ' (optional)'}
                        </label>
                        <input
                          type="text"
                          id="region"
                          placeholder="e.g. us-east-1, eu-west-3"
                          value={s3Fields.region}
                          onChange={(e) => {
                            setS3Fields((prev) => ({ ...prev, region: e.target.value }));
                            setValidationErrors((prev) => ({ ...prev, region: undefined }));
                          }}
                          className={`w-full px-3 py-2 border ${
                            validationErrors.region ? 'border-red-500' : 'border-gray-300'
                          } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        />
                        {validationErrors.region && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.region}</p>
                        )}
                        {isPureAWS && !s3Fields.endpoint && (
                          <p className="mt-1 text-xs text-gray-500">Required when using AWS S3</p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Storage Account - only for Azure */}
                  {newUnit.storageType === ApiTypes.StorageType.BLOB && (
                    <div>
                      <label
                        htmlFor="storage-account"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Storage Account *
                      </label>
                      <input
                        type="text"
                        id="storage-account"
                        placeholder="Enter storage account"
                        value={azureFields.storageAccount}
                        onChange={(e) => {
                          setAzureFields((prev) => ({ ...prev, storageAccount: e.target.value }));
                          setValidationErrors((prev) => ({ ...prev, storageAccount: undefined }));
                        }}
                        className={`w-full px-3 py-2 border ${
                          validationErrors.storageAccount ? 'border-red-500' : 'border-gray-300'
                        } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                      />
                      {validationErrors.storageAccount && (
                        <p className="mt-1 text-sm text-red-600">
                          {validationErrors.storageAccount}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100 flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    // Return to provider selection
                    setShowAddBucketForm(false);
                    setShowProviderSelection(true);
                    resetForm();
                    setValidationErrors({});
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateUnit}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Add bucket
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md mt-8 mb-8 border border-[#E9EAEB]">
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr
                  className="border-b border-gray-200"
                  style={{ backgroundColor: 'white', height: '44px' }}
                >
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Creation
                  </th>
                  <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Calls
                  </th>
                  <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Changes
                  </th>
                  <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Down
                  </th>
                  <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Up
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 text-gray-700 text-center">
                      Loading storage units...
                    </td>
                  </tr>
                ) : units.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 text-gray-500 text-center">
                      No buckets found
                    </td>
                  </tr>
                ) : (
                  paginatedUnits.map((unit: ApiTypes.StorageUnit) => (
                    <tr
                      key={unit.id}
                      className="hover:bg-gray-50 cursor-pointer transition-all"
                      style={{ height: '72px' }}
                      onClick={() => {
                        // Naviguer vers la page de détails de l'unité
                        navigate(`/cloud-storage/buckets/${unit.id}`);
                      }}
                    >
                      <td className="px-4 py-4" style={{ maxWidth: '250px' }}>
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {unit.storageType === ApiTypes.StorageType.S3 && (
                              <img
                                src={logoAws}
                                alt="AWS S3"
                                className="h-6 w-6"
                                style={{ maxHeight: '24px', maxWidth: '24px' }}
                              />
                            )}
                            {unit.storageType === ApiTypes.StorageType.GCS && (
                              <img
                                src={logoGoogle}
                                alt="Google Cloud Storage"
                                className="h-6 w-6"
                                style={{ maxHeight: '24px', maxWidth: '24px' }}
                              />
                            )}
                            {unit.storageType === ApiTypes.StorageType.BLOB && (
                              <img
                                src={logoAzure}
                                alt="Azure Blob Storage"
                                className="h-6 w-6"
                                style={{ maxHeight: '24px', maxWidth: '24px' }}
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0" style={{ overflow: 'hidden' }}>
                            <Typography
                              variant="body1"
                              style={{ 
                                color: '#181D27',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={unit.name}
                            >
                              {unit.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{ 
                                color: '#717680',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              className="text-xs"
                            >
                              {unit.storageType === ApiTypes.StorageType.BLOB
                                ? `${unit.key}@${unit.bucket}`
                                : unit.bucket}
                            </Typography>
                          </div>
                        </div>
                      </td>
                      {/* Colonne "Creation" - Affiche la date de création */}
                      <td className="hidden md:table-cell px-4 py-4 truncate">
                        <Typography variant="body2" style={{ color: '#717680', fontSize: '13px' }}>
                          {(() => {
                            const createdDate =
                              (unit as any).createdAt ||
                              (unit as any).created_at ||
                              (unit as any).timestamp;
                            if (createdDate) {
                              const date = new Date(createdDate);
                              return date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              });
                            }
                            return '-';
                          })()}
                        </Typography>
                      </td>
                      <td className="hidden lg:table-cell px-4 py-4 truncate">
                        <Typography variant="body1" style={{ color: '#717680' }}>
                          {unit.storageType}
                        </Typography>
                      </td>
                      {/* Colonne "Calls" - Affiche le nombre d'appels API */}
                      <td className="hidden xl:table-cell px-4 py-4 truncate">
                        <Typography variant="body1" style={{ color: '#717680' }}>
                          {unitStats[unit.id]?.calls?.toLocaleString() || '0'}
                        </Typography>
                      </td>
                      {/* Colonne "Changes" - Affiche les changements de stockage */}
                      <td className="hidden xl:table-cell px-4 py-4 truncate">
                        <Typography variant="body1" style={{ color: '#717680' }}>
                          {unitStats[unit.id]?.storageChangeFormatted || '0 GB'}
                        </Typography>
                      </td>
                      {/* Colonne "Down" - Affiche les octets téléchargés */}
                      <td className="hidden xl:table-cell px-4 py-4 truncate">
                        <Typography variant="body1" style={{ color: '#717680' }}>
                          {unitStats[unit.id]?.downloadedBytesFormatted || '0 GB'}
                        </Typography>
                      </td>
                      {/* Colonne "Up" - Affiche les octets uploadés */}
                      <td className="hidden xl:table-cell px-4 py-4 truncate">
                        <Typography variant="body1" style={{ color: '#717680' }}>
                          {unitStats[unit.id]?.uploadedBytesFormatted || '0 GB'}
                        </Typography>
                      </td>

                      <td className="px-4 py-4 truncate">
                        {nodeStatuses[unit.id] ? (
                          <Tooltip
                            title={
                              <div className="p-2 bg-white rounded shadow-md">
                                <Typography
                                  variant="subtitle2"
                                  className="font-bold mb-1 text-gray-900"
                                >
                                  Node Status
                                </Typography>
                                <div className="space-y-1">
                                  {nodeStatuses[unit.id].noDataAvailable ? (
                                    <div className="text-gray-500 italic">No data available</div>
                                  ) : nodeStatuses[unit.id].nodes.length === 0 ? (
                                    <div className="text-gray-500 italic">No nodes connected</div>
                                  ) : (
                                    <>
                                      {/* Seulement trois lignes avec le nombre de nœuds dans chaque état */}
                                      <div className="flex items-center">
                                        <span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
                                        <span className="text-green-700">
                                          {nodeStatuses[unit.id].statusCounts.O} Online
                                        </span>
                                      </div>
                                      <div className="flex items-center">
                                        <span className="w-2 h-2 rounded-full bg-orange-500 mr-1"></span>
                                        <span className="text-orange-700">
                                          {nodeStatuses[unit.id].statusCounts.M} Warning
                                        </span>
                                      </div>
                                      <div className="flex items-center">
                                        <span className="w-2 h-2 rounded-full bg-red-500 mr-1"></span>
                                        <span className="text-red-700">
                                          {nodeStatuses[unit.id].statusCounts.D} Disconnected
                                        </span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            }
                            arrow
                            placement="top"
                            classes={{ tooltip: 'bg-transparent' }}
                          >
                            <div className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 cursor-help text-xs whitespace-nowrap">
                              {nodeStatuses[unit.id].noDataAvailable ||
                              nodeStatuses[unit.id].nodes.length === 0 ? (
                                <span className="flex items-center">
                                  <span className="w-2 h-2 rounded-full bg-gray-400 mr-1"></span>
                                  <span className="text-gray-600 font-medium">N/A</span>
                                </span>
                              ) : (
                                <span className="flex items-center justify-between w-full">
                                  {/* Afficher le statut principal */}
                                  <span className="flex items-center">
                                    <span
                                      className={`w-2 h-2 rounded-full mr-1 ${
                                        nodeStatuses[unit.id].statusCounts.O > 0
                                          ? 'bg-green-500'
                                          : nodeStatuses[unit.id].statusCounts.M > 0
                                          ? 'bg-orange-500'
                                          : 'bg-red-500'
                                      }`}
                                    ></span>
                                    <span
                                      className={`font-medium text-xs ${
                                        nodeStatuses[unit.id].statusCounts.O > 0
                                          ? 'text-green-700'
                                          : nodeStatuses[unit.id].statusCounts.M > 0
                                          ? 'text-orange-700'
                                          : 'text-red-700'
                                      }`}
                                    >
                                      {nodeStatuses[unit.id].statusCounts.O > 0
                                        ? 'Online'
                                        : nodeStatuses[unit.id].statusCounts.M > 0
                                        ? 'Warning'
                                        : 'Disconnected'}
                                    </span>
                                  </span>
                                  {/* Afficher le résumé des nœuds */}
                                  <span className="text-gray-500 text-xs ml-1">
                                    {nodeStatuses[unit.id].statusCounts.O > 0 && (
                                      <span className="mr-1">
                                        {nodeStatuses[unit.id].statusCounts.O}{' '}
                                        <span className="text-green-600">●</span>
                                      </span>
                                    )}
                                    {nodeStatuses[unit.id].statusCounts.M > 0 && (
                                      <span className="mr-1">
                                        {nodeStatuses[unit.id].statusCounts.M}{' '}
                                        <span className="text-orange-500">●</span>
                                      </span>
                                    )}
                                    {nodeStatuses[unit.id].statusCounts.D > 0 && (
                                      <span>
                                        {nodeStatuses[unit.id].statusCounts.D}{' '}
                                        <span className="text-red-500">●</span>
                                      </span>
                                    )}
                                  </span>
                                </span>
                              )}
                            </div>
                          </Tooltip>
                        ) : (
                          <div
                            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium justify-center ${
                              unit.status === 'ONLINE'
                                ? 'bg-green-100 text-green-800'
                                : unit.status === 'DISCONNECTED'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {unit.status || 'UNKNOWN'}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right relative">
                        <div className="inline-block relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const menu = e.currentTarget.nextElementSibling as HTMLElement;
                              if (menu) {
                                // Fermer tous les autres menus ouverts
                                document.querySelectorAll('.dropdown-menu').forEach((otherMenu) => {
                                  if (otherMenu !== menu) {
                                    otherMenu.classList.add('hidden');
                                  }
                                });

                                menu.classList.toggle('hidden');

                                // Ajuster la position après l'affichage
                                if (!menu.classList.contains('hidden')) {
                                  setTimeout(() => {
                                    const rect = menu.getBoundingClientRect();
                                    const viewportHeight = window.innerHeight;

                                    // Si la popup dépasse en bas de l'écran, la positionner au-dessus
                                    if (rect.bottom > viewportHeight - 20) {
                                      menu.style.top = 'auto';
                                      menu.style.bottom = '100%';
                                      menu.style.marginTop = '0';
                                      menu.style.marginBottom = '4px';
                                    } else {
                                      menu.style.top = '100%';
                                      menu.style.bottom = 'auto';
                                      menu.style.marginTop = '4px';
                                      menu.style.marginBottom = '0';
                                    }
                                  }, 0);
                                }
                              }
                            }}
                            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                            aria-label="More options"
                          >
                            <MoreVertIcon fontSize="small" style={{ fontSize: '18px' }} />
                          </button>
                          <div
                            className="dropdown-menu hidden absolute right-0 bg-white shadow-lg z-10"
                            style={{
                              width: '168px',
                              height: '131px',
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB',
                              padding: '8px 0',
                              top: '100%',
                              marginTop: '4px',
                            }}
                          >
                            <div className="flex flex-col">
                              <button
                                className="flex items-center px-4 py-2 hover:bg-gray-100 w-full text-left"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/cloud-storage/buckets/${unit.id}`);
                                }}
                                style={{
                                  fontWeight: 600,
                                  fontSize: '14px',
                                  color: '#414651',
                                  lineHeight: '20px',
                                }}
                              >
                                <img
                                  src={eyeSvg}
                                  alt="View"
                                  className="mr-3"
                                  style={{ width: '18px', height: '18px' }}
                                />
                                View
                              </button>
                              <button
                                className="flex items-center px-4 py-2 hover:bg-gray-100 w-full text-left"
                                style={{
                                  fontWeight: 600,
                                  fontSize: '14px',
                                  color: '#414651',
                                  lineHeight: '20px',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedUnit(unit);
                                  setNewUnit({
                                    name: unit.name,
                                    bucket: unit.bucket,
                                    storageType: unit.storageType,
                                    status: 'UNKNOWN',
                                    apiKeyId: unit.apiKeyId,
                                  });
                                  switch (unit.storageType) {
                                    case ApiTypes.StorageType.S3:
                                      setS3Fields({
                                        bucket: unit.bucket,
                                        accessKey: unit.key || '',
                                        secret: unit.secret?.key || '',
                                        endpoint: unit.endpoint || '',
                                        region: unit.region || '',
                                      });
                                      break;
                                    case ApiTypes.StorageType.GCS:
                                      setGcsFields({
                                        bucket: unit.bucket,
                                        clientEmail: unit.key || '',
                                        privateKey: unit.secret?.key || '',
                                        endpoint: unit.endpoint || '',
                                      });
                                      break;
                                    case ApiTypes.StorageType.BLOB:
                                      setAzureFields({
                                        storageAccount: unit.key || '',
                                        container: unit.bucket || '',
                                        accessKey: unit.secret?.key || '',
                                      });
                                      break;
                                  }
                                  setOpenEditUnit(true);
                                }}
                              >
                                <img
                                  src={settingsSvg}
                                  alt="Settings"
                                  className="mr-3"
                                  style={{ width: '18px', height: '18px' }}
                                />
                                Settings
                              </button>
                              <button
                                className="flex items-center px-4 py-2 hover:bg-gray-100 w-full text-left"
                                style={{
                                  fontWeight: 600,
                                  fontSize: '14px',
                                  color: '#F04438',
                                  lineHeight: '20px',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedUnit(unit);
                                  setOpenDeleteDialog(true);
                                }}
                              >
                                <img
                                  src={deleteSvg}
                                  alt="Delete"
                                  className="mr-3"
                                  style={{ width: '18px', height: '18px' }}
                                />
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between py-6 px-4">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalFiltered)} of {totalFiltered} {searchQuery ? 'filtered ' : ''}units
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: currentPage === 1 ? '#E5E7EB' : '#617BFF',
                    color: currentPage === 1 ? '#9CA3AF' : 'white',
                  }}
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className="px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                      style={{
                        backgroundColor: currentPage === page ? '#617BFF' : '#F3F4F6',
                        color: currentPage === page ? 'white' : '#374151',
                      }}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: currentPage === totalPages ? '#E5E7EB' : '#617BFF',
                    color: currentPage === totalPages ? '#9CA3AF' : 'white',
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Bucket Dialog */}
      <Dialog
        open={openEditUnit}
        onClose={() => setOpenEditUnit(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow:
              '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: '28px',
            color: '#101828',
            px: 3,
            pt: 3,
            pb: 2,
          }}
        >
          Edit Bucket
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            px: 3,
            pt: 3,
            pb: 3,
            borderColor: '#E9EAEB',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <CommonFields
              name={newUnit.name || ''}
              storageType={newUnit.storageType || ApiTypes.StorageType.S3}
              validationErrors={validationErrors}
              onChange={(field, value) => {
                setNewUnit((prev) => ({ ...prev, [field]: value }));
                setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
              }}
            />

            {/* API Key Selection for Edit */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Key
              </label>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="edit-new-api-key"
                    name="edit-api-key-mode"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    checked={!newUnit.apiKeyId}
                    onChange={() => setNewUnit((prev) => ({ ...prev, apiKeyId: undefined }))}
                  />
                  <label htmlFor="edit-new-api-key" className="ml-3 block text-sm font-medium text-gray-700">
                    Configure new credentials
                  </label>
                </div>

                {availableApiKeys.length > 0 && (
                  <div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="edit-existing-api-key"
                        name="edit-api-key-mode"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        checked={!!newUnit.apiKeyId}
                        onChange={() => {
                          const suitableKey = selectedUnit?.apiKeyId 
                            ? availableApiKeys.find(k => k.id === selectedUnit.apiKeyId) 
                            : availableApiKeys.find(k => k.provider === newUnit.storageType) || availableApiKeys[0];
                          if (suitableKey) {
                            setNewUnit((prev) => ({ ...prev, apiKeyId: suitableKey.id }));
                          }
                        }}
                      />
                      <label htmlFor="edit-existing-api-key" className="ml-3 block text-sm font-medium text-gray-700">
                        Use existing API key
                      </label>
                    </div>
                    
                    {newUnit.apiKeyId && (
                      <div className="mt-2 ml-7">
                        <select
                          value={newUnit.apiKeyId}
                          onChange={(e) => setNewUnit((prev) => ({ ...prev, apiKeyId: e.target.value }))}
                          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md shadow-sm border"
                        >
                          {availableApiKeys.map((key) => (
                            <option key={key.id} value={key.id}>
                              {key.provider} - {key.key.substring(0, 10)}... (Created {new Date(key.createdAt).toLocaleDateString()})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {!newUnit.apiKeyId && (
              <StorageTypeFields
                storageType={newUnit.storageType || ApiTypes.StorageType.S3}
                s3Fields={s3Fields}
                gcsFields={gcsFields}
                azureFields={azureFields}
                validationErrors={validationErrors}
                isEdit={true}
                onChange={(field, value) => {
                  switch (newUnit.storageType) {
                    case ApiTypes.StorageType.S3:
                      setS3Fields((prev) => ({ ...prev, [field]: value }));
                      break;
                    case ApiTypes.StorageType.GCS:
                      setGcsFields((prev) => ({ ...prev, [field]: value }));
                      break;
                    case ApiTypes.StorageType.BLOB:
                      setAzureFields((prev) => ({ ...prev, [field]: value }));
                      break;
                  }

                  setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => setOpenEditUnit(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              borderColor: '#E9EAEB',
              color: '#344054',
              px: 2,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#D1D5DB',
                bgcolor: '#F9FAFB',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditUnit}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              bgcolor: '#617BFF',
              px: 2.5,
              py: 1,
              borderRadius: '8px',
              boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
              '&:hover': {
                bgcolor: '#5A6FE8',
                boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
              },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Transition appear show={openDeleteDialog} as={Fragment}>
        <HeadlessUIDialog
          as="div"
          className="relative z-[1100]"
          onClose={() => setOpenDeleteDialog(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessUIDialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-[#1a1b1e] p-6 text-left align-middle shadow-xl transition-all">
                  <HeadlessUIDialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-white"
                  >
                    Delete Storage Unit
                  </HeadlessUIDialog.Title>
                  <div className="mt-4">
                    <p className="text-sm text-gray-300">
                      Are you sure you want to delete the storage unit "{selectedUnit?.name}"? This
                      action cannot be undone.
                    </p>
                    {selectedUnit?.bucket && (
                      <p className="mt-2 text-sm text-gray-400">Bucket: {selectedUnit.bucket}</p>
                    )}
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <StyledButton
                      text="Cancel"
                      onClick={() => setOpenDeleteDialog(false)}
                      icon={StyledButtonIcon.X}
                    />
                    <StyledButton
                      text="Delete"
                      onClick={() => {
                        if (selectedUnit) {
                          handleDeleteUnit(selectedUnit.id);
                          setOpenDeleteDialog(false);
                        }
                      }}
                      icon={StyledButtonIcon.X}
                      variant="dark"
                    />
                  </div>
                </HeadlessUIDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessUIDialog>
      </Transition>

          </div>
        </div>
      </div>
    </div>
  );
}

export default StorageUnits;
